import type { PresentationGenerationError } from "@/shared/services/presentation-generation/types";
import axios from "axios";

export type GenerationErrorContext = {
  jobId?: string;
  fallbackMessage: string;
};

/**
 * Normalized shape extracted from any error the generation flow can produce:
 * axios HTTP errors from the backend, job-status `error` envelopes, or generic
 * thrown Errors. All fields are optional so callers can render whatever they got.
 */
export type FormattedGenerationError = {
  jobId?: string;
  errorId?: string;
  code?: string;
  step?: string;
  message: string;
  // Raw payload kept around for `console.error` so the user can paste the full
  // backend response (including devMessage/devStack in non-prod) into a chat.
  raw: unknown;
};

const isPresentationGenerationError = (
  value: unknown,
): value is PresentationGenerationError => {
  if (!value || typeof value !== "object") return false;
  const v = value as Record<string, unknown>;
  return (
    "code" in v ||
    "errorId" in v ||
    "message" in v ||
    "step" in v
  );
};

/**
 * Pull a structured error envelope out of whatever the generation flow throws.
 *  - Axios errors with a JSON response body: read backend's `{ errorId, code, data, message }`.
 *  - Job-status errors (the `error` field on a failed PresentationGenerationJobStatus):
 *    the flow hook wraps these in `new Error(error.message)` then attaches the original
 *    via `.cause`, so we look there too.
 *  - Plain Errors: fall back to `error.message`.
 */
export const formatGenerationError = (
  error: unknown,
  ctx: GenerationErrorContext,
): FormattedGenerationError => {
  // Axios HTTP error from the backend (enqueue / status / result endpoints).
  if (axios.isAxiosError(error)) {
    const data = error.response?.data as
      | {
          errorId?: string;
          code?: string;
          message?: string;
          data?: { field?: string };
        }
      | undefined;
    return {
      jobId: ctx.jobId,
      errorId: data?.errorId,
      code: data?.code,
      step: data?.data?.field,
      message: data?.message || error.message || ctx.fallbackMessage,
      raw: error.response?.data ?? { message: error.message },
    };
  }

  // Job-failure envelope smuggled through `Error.cause` by the flow hook.
  if (error instanceof Error) {
    const cause = (error as Error & { cause?: unknown }).cause;
    if (isPresentationGenerationError(cause)) {
      return {
        jobId: ctx.jobId,
        errorId: cause.errorId,
        code: cause.code,
        step: cause.step,
        message: cause.message || error.message || ctx.fallbackMessage,
        raw: cause,
      };
    }
    return {
      jobId: ctx.jobId,
      message: error.message || ctx.fallbackMessage,
      raw: { message: error.message, name: error.name },
    };
  }

  if (isPresentationGenerationError(error)) {
    return {
      jobId: ctx.jobId,
      errorId: error.errorId,
      code: error.code,
      step: error.step,
      message: error.message || ctx.fallbackMessage,
      raw: error,
    };
  }

  return {
    jobId: ctx.jobId,
    message: ctx.fallbackMessage,
    raw: error,
  };
};

/**
 * Format a `FormattedGenerationError` as a multiline string suitable for a
 * react-toastify `toast.error` call. Skips lines whose value is missing so a
 * minimal failure (just `message`) still renders cleanly.
 */
export const renderGenerationErrorToast = (
  e: FormattedGenerationError,
): string => {
  const lines: string[] = ["Generation failed"];
  const codeBits: string[] = [];
  if (e.code) codeBits.push(e.code);
  if (e.step) codeBits.push(`at step ${e.step}`);
  if (codeBits.length > 0) lines.push(codeBits.join(" "));
  if (e.errorId) lines.push(`errorId: ${e.errorId}`);
  if (e.jobId) lines.push(`jobId: ${e.jobId}`);
  lines.push(e.message);
  return lines.join("\n");
};
